import { useState, useEffect } from 'react'
import { useUserStore } from 'Store/User'
import { useChartStore, ProjectStatusData } from 'Store/chart'
import moment from 'moment'
import { shallow } from 'zustand/shallow'
import { formatDashboardEndDate, formatDashboardStartDate } from 'Utils/dashboardDate'

const ViewModel = () => {
  const INITIAL_START_DATE = moment()
    .tz('Asia/Bangkok')
    .subtract(3, 'months')
    .startOf('month')
    .toDate()
  const INITIAL_END_DATE = moment().tz('Asia/Bangkok').toDate()

  const [startDate, setStartDate] = useState<Date>(INITIAL_START_DATE)
  const [endDate, setEndDate] = useState<Date>(INITIAL_END_DATE)
  const [dataType, setDataType] = useState<'month' | 'daily'>('month')
  const dataTypeOptions = [
    { label: 'รายเดือน', value: 'month' },
    { label: 'รายวัน', value: 'daily' },
  ]
  const { rawSales, getSales } = useUserStore(
    state => ({
      rawSales: state.data || [],
      getSales: state.getAll,
    }),
    shallow
  )
  const [jobStatisticFilterData, setJobStatisticFilterData] = useState<{
    year: number
    saleUserId?: string | null
  }>({
    year: moment().tz('Asia/Bangkok').year(),
    saleUserId: null,
  })
  const yearOptions = Array.from({ length: 10 }).map((_, index) => {
    const year = moment().tz('Asia/Bangkok').year() - index
    return { label: year.toString(), value: year }
  })
  const sales = rawSales
    .filter(u => u.roleId === 7)
    .map(s => ({
      label: s.fullName,
      value: s.id,
    }))

  const [allPileChartData, setAllPileChartData] = useState<{
    categories: string[]
    data: number[]
  }>({
    categories: [],
    data: [],
  })
  const [successPileData, setsuccessPileData] = useState<{
    categories: string[]
    data: number[]
  }>({
    categories: [],
    data: [],
  })

  const [unsuccessPieChartData, setUnsuccessPieChartData] = useState<{
    categories: string[]
    data: number[]
  }>({
    categories: [],
    data: [],
  })

  const [successfulPileHolesData, setSuccessfulPileHolesData] = useState<{
    categories: string[]
    data: any[]
  }>({
    categories: [],
    data: [],
  })

  const [StatsTypeOfPile, setStatsTypeOfPile] = useState<{
    categories: string[]
    series: Array<{
      name: string
      data: number[]
    }>
  }>({
    categories: [],
    series: [],
  })

  const [issueData, setIssueData] = useState<{
    categories: string[]
    data: number[]
  }>({
    categories: [],
    data: [],
  })

  const [projectStatusData, setProjectStatusData] = useState<ProjectStatusData>({
    projects: [],
    data: [],
  })

  const [jobStatisticsData, setJobStatisticsData] = useState<any>({})

  const fetchAllPileData = async () => {
    try {
      const filterStartDate = startDate
      const filterEndDate = endDate

      // Only make API call if both dates are provided
      if (!filterStartDate || !filterEndDate) {
        return
      }

      const { success: allSuccess, data: allData } = await useChartStore.getState().getAll(
        moment(filterStartDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .toISOString(),
        moment(filterEndDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 999,
          })
          .toISOString()
      )
      if (allSuccess && allData) {
        const colors = ['#FF5B5B', '#2D9CDB', '#F2C94C', '#6FCF97', '#9B51E0']
        const responseData = allData as unknown as { categories: string[]; data: number[] }
        const transformedData = {
          categories: responseData.categories || [],
          data: responseData.data || [],
          colors: colors.slice(0, responseData.categories?.length || 0),
        }
        setAllPileChartData(transformedData)
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  const fetchSuccessfulPileData = async () => {
    try {
      const filterStartDate = startDate
      const filterEndDate = endDate

      // Only make API call if both dates are provided
      if (!filterStartDate || !filterEndDate) {
        return
      }

      const { success, data } = await useChartStore.getState().getSuccessful(
        moment(filterStartDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .toISOString(),
        moment(filterEndDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 999,
          })
          .toISOString()
      )
      if (success && data) {
        const colors = ['#FF5B5B', '#2D9CDB']
        const responseData = data as unknown as { categories: string[]; data: number[] }
        const transformedData = {
          categories: responseData.categories || [],
          data: responseData.data || [],
          colors: colors,
        }
        setsuccessPileData(transformedData)
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  const fetchUnsuccessfulPileData = async () => {
    try {
      const filterStartDate = startDate
      const filterEndDate = endDate

      // Only make API call if both dates are provided
      if (!filterStartDate || !filterEndDate) {
        return
      }

      const { success: unsuccessSuccess, data: unsuccessData } = await useChartStore
        .getState()
        .getUnsuccessful(
          moment(filterStartDate)
            .tz('Asia/Bangkok')
            .set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .toISOString(),
          moment(filterEndDate)
            .tz('Asia/Bangkok')
            .set({
              hour: 23,
              minute: 59,
              second: 59,
              millisecond: 999,
            })
            .toISOString()
        )
      if (unsuccessSuccess && unsuccessData) {
        const colors = ['#FF5B5B', '#2D9CDB', '#F2C94C', '#6FCF97']
        const responseData = unsuccessData as unknown as { categories: string[]; data: number[] }
        const transformedData = {
          categories: responseData.categories || [],
          data: responseData.data || [],
          colors: colors.slice(0, responseData.categories?.length || 0),
        }
        setUnsuccessPieChartData(transformedData)
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  const fetchSuccessfulPileHolesData = async () => {
    try {
      const filterStartDate = startDate
      const filterEndDate = endDate

      // Only make API call if both dates are provided
      if (!filterStartDate || !filterEndDate) {
        return
      }

      const { success, data } = await useChartStore.getState().getSuccessfulPileHolesTotal(
        moment(filterStartDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        moment(filterEndDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 999,
          })
          .format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        [],
        dataType
      )
      if (success && data) {
        const colors = ['#FF5B5B', '#2D9CDB']
        const responseData = data as unknown as { categories: string[]; series: any[] }
        const transformedData = {
          categories: responseData.categories || [],
          data: responseData.series || [],
          colors: colors,
        }
        setSuccessfulPileHolesData(transformedData)
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  const fetchOtherData = async () => {
    try {
      // Fetch issue data from API
      const { success: issueSuccess, data: issueDataResponse } = await useChartStore
        .getState()
        .getIssue(formatDashboardStartDate(startDate), formatDashboardEndDate(endDate))
      if (issueSuccess && issueDataResponse) {
        const colors = ['#b399fd']

        const category_state: Record<string, string> = {
          MACHINE_BREAK: 'เครื่องจักรเสียหาย',
          LOSS_TIME: 'รอลูกค้า',
          RAIN: 'ฝนตก',
          WAITING_SUMMARY: 'รอสรุปความยาว',
          RESTORATION: 'ฟื้นฟู',
          UNDERGROUND_OBSTACLE: 'อุปสรรคใต้ดิน',
        }

        const responseData = issueDataResponse as unknown as {
          categories: string[]
          data: number[]
        }
        const transformedData = {
          categories: (responseData.categories || []).map(
            category => category_state[category] || category
          ),
          data: responseData.data || [],
          colors: colors,
        }
        setIssueData(transformedData)
      }

      // Fetch project status data from API
      const { success: statusSuccess, data: statusData } = await useChartStore.getState().getStatus(
        moment(startDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .toISOString(),
        moment(endDate)
          .tz('Asia/Bangkok')
          .set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 999,
          })
          .toISOString()
      )
      if (statusSuccess && statusData) {
        setProjectStatusData(statusData)
      }

      // Fetch job statistics type data from API
      const { success: jobTypeSuccess, data: jobTypeData } = await useChartStore
        .getState()
        .getJobStatisticsType(
          moment(startDate)
            .tz('Asia/Bangkok')
            .set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .toISOString(),
          moment(endDate)
            .tz('Asia/Bangkok')
            .set({
              hour: 23,
              minute: 59,
              second: 59,
              millisecond: 999,
            })
            .toISOString(),
          dataType
        )
      if (jobTypeSuccess && jobTypeData) {
        const typedData = jobTypeData as unknown as {
          categories: string[]
          series: Array<{
            name: string
            data: number[]
          }>
        }
        setStatsTypeOfPile(typedData)
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  const fetchProjectSalePerformanceData = async () => {
    try {
      const { success, data } = await useChartStore.getState().getProjectSalesPerformance({
        year: jobStatisticFilterData.year,
        saleUserId: jobStatisticFilterData.saleUserId || undefined,
        dataType: dataType,
      })
      if (success && data) {
        setJobStatisticsData(data)
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  const fetchMasterData = async () => {
    try {
      await getSales({})
    } catch (err: any) {
      console.log(err)
    }
  }

  const fetchData = () => {
    fetchUnsuccessfulPileData()
    fetchSuccessfulPileData()
    fetchAllPileData()
    fetchOtherData()
    fetchSuccessfulPileHolesData()
  }

  useEffect(() => {
    fetchMasterData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, dataType])

  useEffect(() => {
    fetchProjectSalePerformanceData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatisticFilterData, dataType])

  return {
    // Data
    allPileChartData,
    successPileData,
    unsuccessPieChartData,
    StatsTypeOfPile,
    issueData,
    projectStatusData,
    jobStatisticsData,
    successfulPileHolesData,
    // Filters Options
    jobStatisticFilterData,
    setJobStatisticFilterData,
    sales,
    yearOptions,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    dataTypeOptions,
    dataType,
    setDataType,
  }
}

export default ViewModel
